import { useEffect, useState } from 'react';

import { ensureLibPhonenumberLoaded } from 'apps/promo/helpers/libphonenumberLoader';
import { DEFAULT_MASK, getMobileIntlMaskForCountry } from 'apps/promo/services/phoneMaskService';

type Iso2Code = string | undefined;

interface MaskState {
    defaultMask: string;
    masks: Record<string, string>;
    iso2Ready: Iso2Code;
}

interface CountryMobileMaskResult {
    defaultMask: string;
    masks: Record<string, string>;
    isReady: boolean;
    isMaskReadyForIso2: boolean;
    effectiveIso2: string | undefined;
}

const ISO2_ALIAS_FOR_PHONEINPUT: Record<string, string> = { cc: 'au', cx: 'au' };

export const useCountryMobileMask = (iso2?: string): CountryMobileMaskResult => {
    const [ready, setReady] = useState(false);
    const [maskData, setMaskData] = useState<MaskState>({ defaultMask: DEFAULT_MASK, masks: {}, iso2Ready: undefined });

    useEffect(() => {
        let cancelled = false;

        void ensureLibPhonenumberLoaded().then((isLoaded) => {
            if (!cancelled && isLoaded) setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!ready || !iso2) return;

        void (async () => {
            const effectiveIso2 = ISO2_ALIAS_FOR_PHONEINPUT[iso2] ?? iso2;
            const mask = await getMobileIntlMaskForCountry(effectiveIso2);
            if (cancelled) return;
            setMaskData({
                defaultMask: mask,
                masks: { [effectiveIso2]: mask },
                iso2Ready: iso2,
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [iso2, ready]);

    const isMaskReadyForIso2 = Boolean(ready && maskData.iso2Ready && maskData.iso2Ready === iso2);
    const effectiveIso2 = iso2 ? (ISO2_ALIAS_FOR_PHONEINPUT[iso2] ?? iso2) : undefined;

    return {
        defaultMask: maskData.defaultMask,
        masks: maskData.masks,
        isReady: ready,
        isMaskReadyForIso2,
        effectiveIso2,
    };
};
